/** What the rail's search finds, and in what order — pure, so it is tested without a router.
 *
 * **What it searches, and what it deliberately does not.** It finds *places*: a screen by its name
 * (the rail's own list, handed in, so a label is written once), and a Help section by its heading or
 * its words (`helpContent.ts`). Its last result is always the same, and it is the honest one: the
 * emissions register already filters by text (`?q=`, `search.ts`), so the way to search *records* is
 * to open the register on the query — not a second index of figures that could disagree with the
 * register's own filter.
 *
 * It does **not** query the API. A search over figures would be a read that could fail, and a
 * "no results" for a failed read is the defect this whole gate exists to remove; a search over
 * local, static text cannot fail that way, and it says so by always having a last result.
 *
 * Matching is word-wise AND on lower-cased text: `data region` finds a section containing both
 * words, in any order. Headings rank above body text, because a reader who types a topic's name
 * wants the topic, not the paragraph that happens to mention it.
 */

import type { HelpHit } from "./helpContent";

export interface SearchTarget {
  kind: "screen" | "help" | "register";
  label: string;
  /** What kind of place, said in words: "Screen", "Help · How your data is processed". */
  detail: string;
  to: string;
  /** Help only: the `?tab=` to open. */
  tab?: string;
  /** Register only: the text to filter by. */
  q?: string;
}

export const MAX_SCREENS = 5;
export const MAX_HELP = 4;

function words(text: string): string[] {
  return text.toLowerCase().split(/\s+/).filter(Boolean);
}

function hasAll(haystack: string, needles: readonly string[]): boolean {
  const lowered = haystack.toLowerCase();
  return needles.every((needle) => lowered.includes(needle));
}

/** Results for `query`; nothing at all for a blank one (a list of everything is not a search). */
export function searchTargets(
  query: string,
  screens: ReadonlyArray<{ to: string; label: string }>,
  help: readonly HelpHit[],
): SearchTarget[] {
  const needles = words(query);
  if (needles.length === 0) return [];

  const found: SearchTarget[] = [];

  for (const screen of screens
    .filter((entry) => hasAll(entry.label, needles))
    .slice(0, MAX_SCREENS)) {
    found.push({ kind: "screen", label: screen.label, detail: "Screen", to: screen.to });
  }

  const byHeading = help.filter((hit) => hasAll(hit.heading, needles));
  const byText = help.filter(
    (hit) => !byHeading.includes(hit) && hasAll(`${hit.heading} ${hit.text}`, needles),
  );
  for (const hit of [...byHeading, ...byText].slice(0, MAX_HELP)) {
    found.push({
      kind: "help",
      label: hit.heading,
      detail: `Help · ${hit.topic}`,
      to: "/help",
      tab: hit.tab,
    });
  }

  found.push({
    kind: "register",
    label: `Search the emissions register for “${query.trim()}”`,
    detail: "Records",
    to: "/emissions",
    q: query.trim(),
  });
  return found;
}
